import Accordion from "@/common/components/Accordion";
import Alert from "@/common/components/Alert";
import Button from "@/common/components/Button";
import { AlertCircleIcon } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useClientBillDynamicGroupables } from "../../hooks/billDynamicGroupables/useClientBillDynamicGroupables";
import { BillDynamicGroupablesData } from "../../types/billDynamicGroupables.type";
import Skeleton from "@/common/components/Skeleton";
import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";
import useUserPermissions from "@/common/hooks/useUserPermissions";

const BillDynamicGroupables = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const {
    data: billDynamicGroupables,
    isPending: billDynamicGroupablesIsPending,
  } = useClientBillDynamicGroupables<BillDynamicGroupablesData[]>({
    companyId: id,
  });

  if (!isUserPermissionsPending && !hasUserPermission('Admin::Client::Companies::BillDynamicGroupablesFilter')) {
    return null;
  }

  return (
    <Accordion
      defaultOpen
      name="bill_dynamic_groupables"
      head={(
        <div className="flex items-center gap-x-3">
          <div onClick={(e) => e.stopPropagation()}>
            <Button
              name="edit_bill_dynamic_groupables"
              operationKlassToCheckPermission="Admin::Client::Companies::UpsertBillDynamicGroupables"
              onClick={() => navigate('billing-dynamic-groupables')}
            >
              Editar
            </Button>
          </div>

          <p className='text-xl'>
            Agrupamento Dinâmico da Fatura
          </p>
        </div>
      )}
      triggerClassName="py-0"
      bodyClassName="pt-4 pb-0"
      body={
        <div className='flex flex-col gap-4'>
          <div className="flex flex-col gap-4">
            {billDynamicGroupablesIsPending && (
              <div className="flex flex-col gap-2">
                <Skeleton width={100} />

                <div className="flex gap-4">
                  <div className="flex flex-col gap-1">
                    <Skeleton width={60} height={16} />

                    <Skeleton width={200} />
                  </div>

                  <div className="flex flex-col gap-1">
                    <Skeleton width={70} height={16} />

                    <Skeleton width={250} />
                  </div>
                </div>
              </div>
            )}

            {!billDynamicGroupablesIsPending && (
              <>
                {billDynamicGroupables.length > 0 ? billDynamicGroupables.map((groupable, index) => (
                  <div key={groupable.id} className="flex flex-col gap-1">
                    <p className="text-md">
                      Fatura {index + 1}
                    </p>

                    <div className="flex gap-3">
                      {groupable.product_categories.length > 0 && (
                        <div className="flex flex-col">
                          <p className="text-xs italic text-white-400">
                            Serviços
                          </p>

                          <p className="text-xs" data-testid={`bill_dynamic_groupable_${index}_categories`}>
                            {groupable.product_categories.map(({ display_name }) => display_name).join(', ')}
                          </p>
                        </div>
                      )}

                      {groupable.products.length > 0 && (
                        <div className="flex flex-col">
                          <p className="text-xs italic text-white-400">
                            Produtos
                          </p>

                          <p className="text-xs" data-testid={`bill_dynamic_groupable_${index}_products`}>
                            {groupable.products.map(({ display_name }) => display_name).join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )) : (
                  <EmptyDataMessage
                    message="Não há agrupamentos dinâmicos cadastrados na empresa"
                    className="overflow-hidden m-auto h-min"
                    imageHeight={180}
                    dataTestId="empty_bill_dynamic_groupables"
                  />
                )}
              </>
            )}
          </div>

          {billDynamicGroupables.length > 0 && (
            <Alert
              variant="warning"
              Icon={AlertCircleIcon}
              containerClassName="bg-transparent p-0"
            >
              <div className="flex flex-col">
                <p className="text-xs">
                  Demais faturas serao emitidas pelo agrupamento definido no contrato.
                </p>

                <p className="text-xs italic font-light">
                  Atente-se, os Serviços e Produtos específicados em faturas acima não entrarão nas demais faturas.
                </p>
              </div>
            </Alert>
          )}
        </div>
      }
    />
  );
}

export default BillDynamicGroupables;
